import { create } from "zustand";
import { devtools } from "zustand/middleware";
import type { NotificationSortBy, NotificationSortOrder, NotificationViewType } from "../types";

interface NotificationsUIState {
  // View state
  viewType: NotificationViewType;
  sortBy: NotificationSortBy;
  sortOrder: NotificationSortOrder;

  // Modal states
  preferencesDialogOpen: boolean;
  bulkActionsDialogOpen: boolean;

  // Selection state
  selectedIds: string[];
  selectAllActive: boolean;

  // UI states
  sidebarCollapsed: boolean;

  // Actions
  setViewType: (viewType: NotificationViewType) => void;
  setSorting: (sortBy: NotificationSortBy, sortOrder: NotificationSortOrder) => void;

  // Modal actions
  openPreferencesDialog: () => void;
  closePreferencesDialog: () => void;
  openBulkActionsDialog: () => void;
  closeBulkActionsDialog: () => void;

  // Selection actions
  setSelectedIds: (ids: string[]) => void;
  toggleSelection: (id: string) => void;
  selectAll: (ids: string[]) => void;
  clearSelection: () => void;

  // UI actions
  toggleSidebar: () => void;
  setSidebarCollapsed: (collapsed: boolean) => void;
}

export const useNotificationsUIStore = create<NotificationsUIState>()(
  devtools(
    (set, _get) => ({
      // Initial state
      viewType: "list",
      sortBy: "createdAt",
      sortOrder: "desc",

      preferencesDialogOpen: false,
      bulkActionsDialogOpen: false,

      selectedIds: [],
      selectAllActive: false,

      sidebarCollapsed: false,

      // View actions
      setViewType: (viewType) => set({ viewType }),

      setSorting: (sortBy, sortOrder) =>
        set({
          sortBy,
          sortOrder,
        }),

      // Modal actions
      openPreferencesDialog: () => set({ preferencesDialogOpen: true }),
      closePreferencesDialog: () => set({ preferencesDialogOpen: false }),
      openBulkActionsDialog: () => set({ bulkActionsDialogOpen: true }),
      closeBulkActionsDialog: () => set({ bulkActionsDialogOpen: false }),

      // Selection actions
      setSelectedIds: (selectedIds) => set({ selectedIds }),

      toggleSelection: (id) =>
        set((state) => ({
          selectedIds: state.selectedIds.includes(id)
            ? state.selectedIds.filter((selectedId) => selectedId !== id)
            : [...state.selectedIds, id],
        })),

      selectAll: (ids) =>
        set({
          selectedIds: ids,
          selectAllActive: true,
        }),

      clearSelection: () =>
        set({
          selectedIds: [],
          selectAllActive: false,
        }),

      // UI actions
      toggleSidebar: () =>
        set((state) => ({
          sidebarCollapsed: !state.sidebarCollapsed,
        })),

      setSidebarCollapsed: (sidebarCollapsed) => set({ sidebarCollapsed }),
    }),
    { name: "notifications-ui-store" }
  )
);
